import { addDisplayLabel, useDSDFiles } from "@/hooks/use-dsd-files";
import { DsdFile } from "@/types/dsd";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { pushMisclickConfirmModal } from "@/components/modals/misclick-confirm-modal";
import { useTableCells, useTableId } from "@/components/mapping/hooks";
import { ForwardProgressStep } from "@/components/workspace/forward/forward-progress-step";
import {
  useInitializeRollForwardInfo,
  useRollForwardInfo,
} from "@/hooks/mutations/use-roll-forward-info";
import {
  isForwardRunning,
  useForwardStatus,
} from "@/hooks/mutations/use-forward-status";
import { useIsCurrentSheetAnalyzing } from "@/hooks/use-is-current-sheet-analyzing";
import { useIsCurrentSheetTaggingTarget } from "@/hooks/use-is-current-sheet-tagging-target";
import { useIsWorkbookAnalyzed } from "@/hooks/use-is-workbook-analyzed";
import { useCanRunWorkbookWideAction } from "@/hooks/use-can-run-workbook-wide-action";
import useTablesTaggingStatus from "@/hooks/queries/use-tables-tagging-status";
import { useWorkbookId } from "@/hooks/use-workbook";
import { useModalStore } from "@/stores/modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import { Button } from "@/toggle-ui-kit/button";
import { useIsMutating } from "@tanstack/react-query";
import { ChevronDown, Download, ListChecks, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ForwardModalDescription,
  ForwardModalHeader,
} from "./forward-modal-chrome";
import { ReportSelectStep } from "./report-select-step";

const RollForwardModal = ({
  mode,
}: {
  mode: "single" | "all" | "progress";
}) => {
  const [step, setStep] = useState<1 | 2 | null>(null);
  const [selectedWorkbook, setSelectedWorkbook] = useState<DsdFile | null>(
    null,
  );
  const { pop } = useModalStore((state) => state.modals);
  const { data: workbookId } = useWorkbookId();
  const { data: tableId } = useTableId();
  const { data: rollForwardInfo, isLoading: isInfoLoading } =
    useRollForwardInfo(workbookId ?? undefined);
  const { mutate: initialize, isPending } = useInitializeRollForwardInfo();

  // 현재 워크북을 뺀 나머지 보고서 목록 — 최신 업로드 순.
  const {
    data: reports,
    isLoading: isReportsLoading,
    isError: isReportsError,
    isFetchedAfterMount,
  } = useDSDFiles({
    enabled: !!workbookId,
    select: (data) =>
      addDisplayLabel(
        data.filter((workbook) => workbook.id !== workbookId),
      ).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
  });

  // 이전에 고른 이월 짝(roll-forward-info)이 있으면 워크북 선택(Step1)을 건너뛰고 바로
  // 표 선택(Step2)으로 — 매번 워크북부터 다시 고르지 않게. progress 모드도 짝이 없으면
  // Step1(워크북 선택)부터 시작한다.
  useEffect(() => {
    if (step !== null || isInfoLoading) return;
    setStep(rollForwardInfo && rollForwardInfo.items.length > 0 ? 2 : 1);
  }, [step, isInfoLoading, rollForwardInfo]);

  if (step === null) {
    return (
      <ModalWrapper className="flex h-[550px] max-h-[calc(100vh-48px)] w-[600px] flex-col gap-0 overflow-hidden rounded-[8px] border-0 bg-background-base p-0 shadow-xxlarge ring-1 ring-inset ring-line-normal">
        <ForwardModalHeader title="XBRL 태그 불러오기" />
        <ForwardModalDescription>
          이전 보고서의 태깅 정보를 덮어씁니다.
        </ForwardModalDescription>
        <div className="flex flex-1 items-center justify-center gap-2 caption-1-regular text-label-alternative">
          <Loader2 className="size-4 animate-spin" />
          불러오는 중...
        </div>
      </ModalWrapper>
    );
  }

  if (step === 2) {
    return (
      <ForwardProgressStep
        sourceWorkbook={selectedWorkbook ?? undefined}
        initialSelectMode={
          mode === "single" ? "single" : mode === "all" ? "all" : "none"
        }
        currentTableId={tableId?.tableId ?? undefined}
        onPrev={() => setStep(1)}
      />
    );
  }

  return (
    <ReportSelectStep
      reports={reports ?? []}
      selectedReport={selectedWorkbook}
      onSelectReport={setSelectedWorkbook}
      isLoading={isReportsLoading || !isFetchedAfterMount}
      isError={isReportsError}
      isConfirming={isPending}
      onCancel={() => pop()}
      onConfirm={() => {
        if (!workbookId) {
          toast.error("에러가 발생했습니다.");
          return;
        }
        if (!selectedWorkbook) {
          toast.error("불러올 파일을 선택해주세요.");
          return;
        }
        if (isPending) {
          return;
        }
        initialize(selectedWorkbook.id, {
          onSuccess: () => setStep(2),
        });
      }}
    />
  );
};

const ForwardButton = () => {
  const { push } = useModalStore((state) => state.modals);
  const { data: workbookId } = useWorkbookId();
  const { data: tableId } = useTableId();
  const isWorkbookAnalyzed = useIsWorkbookAnalyzed(workbookId ?? undefined);
  // "전체 표 불러오기"만 본다 — 단일 표 액션은 아래 시트 단위 게이트 몫이다.
  // 오토태깅 쪽과 같은 훅을 쓴다(WXBRL-2682).
  const canRunWorkbookWide = useCanRunWorkbookWideAction(
    workbookId ?? undefined,
  );
  const isCurrentSheetAnalyzing = useIsCurrentSheetAnalyzing();
  // 일반사항·회계정책·추정 등의 목차 시트에서는 "하나의 표 불러오기"만 잠근다 — 전체·진행상황은 다른 표 몫이다.
  const isCurrentSheetTaggingTarget = useIsCurrentSheetTaggingTarget();
  // 읽기 전용 구독 — 폴링·완료 처리는 페이지 루트의 useForwardCompletionEffect 전담.
  const { data: forwardStatus } = useForwardStatus(workbookId ?? undefined);
  const currentTableStatus = forwardStatus?.tables.find(
    (table) => table.tableId === tableId?.tableId,
  );
  // 현재 표를 포함한 시작 요청(모달 안 mutation)이 도는 동안도 진행 중으로 본다 — 첫 상태
  // 응답 전 공백을 메운다. 다른 표만 고른 요청에는 반응하지 않는다.
  const isEnqueuing =
    useIsMutating({
      mutationKey: ["forward-enqueue", workbookId],
      predicate: (mutation) => {
        const variables = mutation.state.variables as
          | { tableIds?: string[] }
          | undefined;
        // tableIds 가 없으면 워크북 전체 실행이다.
        if (!variables?.tableIds) return true;
        return (
          !!tableId?.tableId && variables.tableIds.includes(tableId.tableId)
        );
      },
    }) > 0;
  // 버튼은 현재 표 기준 — 오토태깅 버튼과 같다.
  const isCurrentTableForwarding =
    isEnqueuing ||
    (currentTableStatus ? isForwardRunning(currentTableStatus) : false);
  // 현재 표는 셀 캐시가 정본이다 — 태깅 뮤테이션이 이 캐시를 무효화하므로 방금 한
  // 태깅도 반영된다. (tables-tagging-status 는 마운트 때 한 번만 받아 금방 낡는다.)
  const { cells } = useTableCells();
  const currentTableHasTags =
    !cells || cells.cells.some((cell) => cell.taggedInfos.tags.length > 0);
  // 워크북 전체는 서버에 물어야 한다. 메뉴를 열 때마다 새로 받고, 받는 동안은 모르는
  // 것으로 보고 묻는다 — 조용히 덮어쓰는 쪽이 더 비싸다.
  const {
    data: taggingStatus,
    isFetching: isTaggingStatusFetching,
    refetch: refetchTaggingStatus,
  } = useTablesTaggingStatus({ workbookId });
  const workbookHasTags =
    isTaggingStatusFetching ||
    !taggingStatus ||
    taggingStatus.tables.some((table) => table.hasAnyTaggedCells);
  // 덮어쓸 태그가 하나도 없으면 되돌릴 것도 없으니 확인 없이 바로 진행한다.
  const hasTagsToOverwrite = (mode: "single" | "all") =>
    mode === "all" ? workbookHasTags : currentTableHasTags;
  const confirmForward = (mode: "single" | "all") => {
    if (!hasTagsToOverwrite(mode)) {
      push(RollForwardModal, { mode });
      return;
    }
    pushMisclickConfirmModal({
      title: "XBRL 태그를 새로 불러올까요?",
      description: (
        <>
          태그를 불러오면 기존 태그가 새 워크북의 태그로 모두 덮어씌워집니다.
          <br />
          시작한 뒤에는 중단하거나 되돌릴 수 없습니다.
        </>
      ),
      confirmLabel: "태그 새로 불러오기",
      onConfirm: () => push(RollForwardModal, { mode }),
    });
  };
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) refetchTaggingStatus();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outlined"
          leftIcon={
            isCurrentTableForwarding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )
          }
          rightIcon={<ChevronDown className="w-4 h-4" />}
          disabled={
            !workbookId || !isWorkbookAnalyzed || isCurrentSheetAnalyzing
          }
        >
          {isCurrentTableForwarding ? "불러오는 중" : "XBRL 태그 불러오기"}
        </Button>
      </DropdownMenuTrigger>
      {/* 메뉴가 닫히며 트리거로 포커스를 되돌리면, 방금 열린 모달의 확인 버튼 포커스를
          빼앗아 Enter 가 먹지 않는다. 이 메뉴의 항목은 모두 모달을 열므로 되돌리지 않는다. */}
      <DropdownMenuContent
        align="end"
        className="min-w-[180px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          disabled={
            !workbookId ||
            !isWorkbookAnalyzed ||
            !tableId?.tableId ||
            isCurrentSheetAnalyzing ||
            !isCurrentSheetTaggingTarget
          }
          onSelect={() => confirmForward("single")}
        >
          <Download className="w-4 h-4" />
          하나의 표 불러오기
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          disabled={!canRunWorkbookWide}
          onSelect={() => confirmForward("all")}
        >
          <Download className="w-4 h-4" />
          전체 표 불러오기
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          disabled={!workbookId}
          onSelect={() => push(RollForwardModal, { mode: "progress" })}
        >
          <ListChecks className="w-4 h-4" />
          진행상황
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ForwardButton;
